import { useState } from "react";
import { HiOutlineChevronDown, HiOutlineX } from "react-icons/hi";
import { PiCheckBold } from "react-icons/pi";
import { useFloating, autoUpdate, offset, flip, shift, useClick, useDismiss, useRole, useInteractions, FloatingPortal } from "@floating-ui/react";

const usePopover = (placement) => {
  const [open, setOpen] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip({ padding: 8 }), shift({ padding: 8 })],
  });
  const interactions = useInteractions([useClick(context), useDismiss(context), useRole(context)]);
  return { open, setOpen, refs, floatingStyles, ...interactions };
};

// A bulk "update": one button that opens a list of values to apply to every selected row.
const EditMenu = ({ field, disabled }) => {
  const { open, setOpen, refs, floatingStyles, getReferenceProps, getFloatingProps } = usePopover("top-start");
  return (
    <>
      <button
        ref={refs.setReference}
        {...getReferenceProps()}
        type="button"
        disabled={disabled}
        data-open={open || undefined}
        className="flex items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-1 text-[13px] font-medium transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 text-gray-700 data-[open=true]:bg-gray-100"
      >
        {field.label}
        <HiOutlineChevronDown className="h-3.5 w-3.5 text-gray-400" />
      </button>
      {open && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, zIndex: 60 }}
            {...getFloatingProps()}
            className="min-w-[220px] overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
          >
            <p className="px-3 pb-1 pt-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-400">{field.label}</p>
            {field.options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  field.onSelect(option.value);
                  setOpen(false);
                }}
                className="flex w-full items-center px-3 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50"
              >
                {option.label}
              </button>
            ))}
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

// A bulk action. Fires immediately, unless given `confirm`, in which case it opens a
// confirmation popover anchored to the button (no full-screen modal for a list action).
// Resting destructive actions stay neutral and only redden on hover; the single brand-blue
// accent is reserved for the primary action.
const ActionButton = ({ action, disabled }) => {
  const { open, setOpen, refs, floatingStyles, getReferenceProps, getFloatingProps } = usePopover("top-end");
  const variantClass =
    action.variant === "primary"
      ? "bg-primary-500 text-white hover:bg-primary-700"
      : action.variant === "danger"
        ? "text-gray-500 hover:bg-red-50 hover:text-red-600"
        : "text-gray-700 hover:bg-gray-100";

  if (!action.confirm) {
    return (
      <button type="button" disabled={disabled} onClick={action.onClick} className={`flex items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-1 text-[13px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${variantClass}`}>
        {action.label}
      </button>
    );
  }

  return (
    <>
      <button ref={refs.setReference} {...getReferenceProps()} type="button" disabled={disabled} data-open={open || undefined} className={`flex items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-1 text-[13px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${variantClass}`}>
        {action.label}
      </button>
      {open && (
        <FloatingPortal>
          <div ref={refs.setFloating} style={{ ...floatingStyles, zIndex: 60 }} {...getFloatingProps()} className="w-64 rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
            <p className="text-sm font-medium text-gray-900">{action.confirm.title}</p>
            {action.confirm.body && <p className="mt-1 text-xs leading-relaxed text-gray-500">{action.confirm.body}</p>}
            <div className="mt-3 flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="rounded-md px-2.5 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100">
                Annuler
              </button>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  action.onClick();
                }}
                className="rounded-md bg-red-600 px-2.5 py-1.5 text-sm font-medium text-white transition-colors hover:bg-red-700"
              >
                {action.confirm.confirmLabel || "Confirmer"}
              </button>
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

const BulkActionBar = ({ count, total, noun = "élément", feminine = false, loading = false, edits = [], actions = [], onClear }) => {
  if (count === 0) return null;

  const plural = (total ?? count) > 1 ? "s" : "";
  const agreement = feminine ? "e" : "";
  const visibleActions = actions.filter((action) => !action.hidden);

  return (
    <div className="animate-slide-up fixed bottom-6 left-1/2 z-40 -translate-x-1/2">
      <div className="flex items-center gap-1 whitespace-nowrap rounded-xl border border-gray-200 bg-white px-1.5 py-1.5 shadow-[0_8px_28px_-6px_rgba(16,24,40,0.16)]">
        <div className="flex items-center gap-2 pl-1 text-[13px]">
          <span className="flex h-5 w-5 items-center justify-center rounded-md bg-gray-100 text-gray-500">
            {loading ? <span className="h-2.5 w-2.5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" /> : <PiCheckBold className="h-2.5 w-2.5" />}
          </span>
          <span>
            <span className="font-semibold tabular-nums text-gray-900">{total != null ? `${count} / ${total}` : count}</span>{" "}
            <span className="text-gray-500">
              {noun}
              {plural} sélectionné{agreement}{plural}
            </span>
          </span>
        </div>

        {edits.length > 0 && (
          <>
            <div className="mx-1 h-5 w-px bg-gray-200" />
            {edits.map((field) => (
              <EditMenu key={field.key} field={field} disabled={loading} />
            ))}
          </>
        )}

        {visibleActions.length > 0 && (
          <>
            <div className="mx-1 h-5 w-px bg-gray-200" />
            {visibleActions.map((action) => (
              <ActionButton key={action.key} action={action} disabled={loading} />
            ))}
          </>
        )}

        <div className="mx-1 h-5 w-px bg-gray-200" />
        <button type="button" onClick={onClear} title="Effacer la sélection (Échap)" className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600">
          <HiOutlineX className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default BulkActionBar;
